import { IFieldProps } from "types/Organisational";

type OrganisationValues = Record<string, string | null | undefined>;

export type OrganisationPrefill = {
  organizational: OrganisationValues;
  shipment: { title: string };
};

/** Shipment details expose organisation values under different keys than the request-form draft. */
const DETAILS_TO_FORM_FIELD_IDS: Record<string, string> = {
  invoice_location: "invoice_location",
  cost_center: "cost_center",
  reason_code_group: "reason_code_group",
  reason_code: "reason_code",
  custom_field1: "custom_field_01",
  custom_field2: "custom_field_02",
  custom_field3: "custom_field_03",
  custom_cost_recovery_field1: "custom_cost_recovery_field1",
  custom_cost_recovery_field2: "custom_cost_recovery_field2",
  custom_longfield_01: "custom_long_field_01",
};

/** Details returns the option label, while the form selects on the option value. */
function resolveFieldValue(fieldId: string, rawValue: string, options: IFieldProps[] | undefined): string {
  const dropdownValues = options?.find((option) => option.field_id === fieldId)?.dropdown_values;
  if (!dropdownValues?.length) return rawValue;

  const match =
    dropdownValues.find((item) => item.value === rawValue) ?? dropdownValues.find((item) => item.key === rawValue);

  return match?.value ?? rawValue;
}

function resolveCostRecoveryValue(rawValue: boolean, options: IFieldProps[] | undefined): string | undefined {
  const dropdownValues = options?.find((option) => option.field_id === "cost_recovery_required")?.dropdown_values;
  const wantedKey = rawValue ? "Yes" : "No";

  return dropdownValues?.find((item) => item.key === wantedKey)?.value ?? String(rawValue);
}

export function buildOrganisationPrefill(
  organization: Record<string, unknown> | undefined,
  options: IFieldProps[] | undefined,
  paymentLocation?: string,
): OrganisationPrefill {
  const organizational: OrganisationValues = { location: paymentLocation ?? "" };

  Object.entries(DETAILS_TO_FORM_FIELD_IDS).forEach(([detailsKey, fieldId]) => {
    const rawValue = organization?.[detailsKey];
    if (typeof rawValue !== "string" || !rawValue) return;
    organizational[fieldId] = resolveFieldValue(fieldId, rawValue, options);
  });

  const costRecoveryRequired = organization?.cost_recovery_required;
  if (typeof costRecoveryRequired === "boolean") {
    organizational.cost_recovery_required = resolveCostRecoveryValue(costRecoveryRequired, options);
  }

  return {
    organizational,
    shipment: {
      title: (organization?.title as string) ?? "",
    },
  };
}
